import {
  focusStateStyles,
  Underline,
  thinnerUnderlineHoverStyles,
} from '@/components/underline';
import { styled, media } from '@/stitches.config';
import { ChevronDown } from '@/components/icons';
import { Close } from '@/components/icons';
import { useState } from 'react';
import { CONFIG } from '../../config';
import { useExperienceStore } from '../../store';
import { CaseButton } from './case-button';
import { ColorIndicator } from './color-indicator';
import { useIsTouch } from '@/hooks/use-is-touch';
import { Timer } from './timer';
import { motion, AnimatePresence } from 'framer-motion';
import { SelectIcon } from './select-icon';
import { LoadingIcon } from './loading-icon';
import { useMedia } from 'react-use';

export function CaseMetaData() {
  const {
    activePalette,
    currentState,
    resetActiveIndex,
    updateState,
    activeIndexes,
    exploreState,
    showContent: stateShowContent,
  } = useExperienceStore((state) => ({
    activePalette: state.activePalette,
    currentState: state.currentState,
    resetActiveIndex: state.resetActiveIndex,
    updateState: state.updateState,
    activeIndexes: state.activeIndexes,
    exploreState: state.exploreState,
    showContent: state.showContent,
  }));

  const [showContent, setShowContent] = useState(true);
  const isTouch = useIsTouch();

  function onClickHandler() {
    resetActiveIndex();
    updateState('explore');
  }

  function onClickHideHandler() {
    setShowContent(!showContent);
  }

  const remainingItems =
    CONFIG.AMOUNT_OF_ITEMS_BEFORE_MATCHING - activeIndexes.length;

  const isWide = useMedia(media.bp3);

  let showContentByDefault;

  if (!isWide) {
    showContentByDefault = !showContent;
  } else {
    showContentByDefault = showContent;
  }

  return (
    <AnimatePresence>
      {currentState === 'explore' &&
        exploreState === 'orb' &&
        activeIndexes.length === 0 && (
          <Container key="explorer-orb">
            <Title
              weight="regular"
              css={{
                color: stateShowContent ? '#6a6c6e' : '$foreground',
              }}
            >
              {isTouch
                ? 'Touch circles to explore'
                : 'Explore our portfolio by color'}
            </Title>
          </Container>
        )}
      jhg
      {currentState === 'explore' &&
        exploreState === 'cluster' &&
        activeIndexes.length === 0 && (
          <Container key="explorer">
            <Title
              weight="regular"
              noMarign
              css={{
                color:
                  exploreState === 'cluster' || stateShowContent
                    ? '$foreground'
                    : '#6a6c6e',
                display: 'flex',
                alignItems: 'center',

                svg: {
                  fill:
                    exploreState === 'cluster' || stateShowContent
                      ? '$foreground'
                      : '#6a6c6e',
                },
              }}
            >
              <SelectIcon activeItems={0} />
              {`${
                isTouch ? 'Touch' : 'Hover over'
              } circles to explore our portfolio by color`}
            </Title>
          </Container>
        )}
      {currentState === 'explore' && activeIndexes.length > 0 && (
        <>
          {remainingItems == 0 ? (
            <Container key="explore-select-colors-1">
              <Title weight="regular" noMarign>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Timer
                    radius={14}
                    key={activeIndexes.toString()}
                    durationInSeconds={
                      CONFIG.CONFIRM_SELECTION_EXPLORE_IN_SECONDS
                    }
                  />
                </div>
              </Title>
            </Container>
          ) : (
            <Container
              key={`explore-select-colors-2-${
                CONFIG.AMOUNT_OF_ITEMS_BEFORE_MATCHING - remainingItems
              }`}
            >
              <SelectIcon
                activeItems={
                  CONFIG.AMOUNT_OF_ITEMS_BEFORE_MATCHING - remainingItems
                }
              />
              <Title weight="regular" noMarign>
                {`${isTouch ? 'Touch' : 'Hover'} ${remainingItems} more ${
                  remainingItems === 1 ? 'color' : 'colors'
                }`}
              </Title>
            </Container>
          )}
        </>
      )}
      {currentState === 'loading' && (
        <Container key="loading">
          <LoadingIcon />
          <Title weight="regular" noMarign>
            matching colors to portfolio image
          </Title>
        </Container>
      )}
      {currentState === 'matched' && activePalette && (
        <SlideContainer hasBackground>
          <div>
            <CloseButtonContainer onClick={onClickHandler}>
              <Close />
            </CloseButtonContainer>

            <HideButton onClick={onClickHideHandler}>
              <Title
                weight="regular"
                isInverted
                css={{
                  marginBottom: 0,
                }}
              >
                {showContentByDefault ? 'Hide meta data' : 'Show meta data'}
              </Title>
              <ChevronDown
                width={20}
                height={20}
                style={{
                  transition: '.2s ease',
                  transform: showContentByDefault
                    ? 'rotate(0deg)'
                    : 'rotate(180deg)',
                }}
              />
            </HideButton>

            {showContentByDefault && (
              <>
                <ItemWrapper>
                  <Title isInverted>Selected colors</Title>
                  <ColorIndicator
                    colors={activePalette.sampledColors.reverse()}
                  />
                </ItemWrapper>

                <ItemWrapper>
                  <Title isInverted>Matched portfolio image</Title>

                  <TextContent isInverted>
                    {activePalette.palette.title}
                  </TextContent>
                </ItemWrapper>
              </>
            )}

            <ItemWrapper
              css={{
                marginBottom: '$4',
              }}
            >
              <CaseButton isInverted />
            </ItemWrapper>

            <ItemWrapper
              isInverted
              css={{
                flexDirection: 'row',
                alignItems: 'center',
                marginBottom: 0,
                marginLeft: '-6px',
              }}
            >
              <Timer durationInSeconds={CONFIG.CASE_GO_AWAY_IN_SECONDS} />
              <TextContent
                style={{
                  marginTop: '3px',
                }}
              >
                <Button onClick={onClickHandler} isInverted>
                  Back to all C°F colors
                  <Underline />
                </Button>
              </TextContent>
            </ItemWrapper>
          </div>
        </SlideContainer>
      )}
    </AnimatePresence>
  );
}

interface ContainerProps {
  children: React.ReactNode;
  isDimmed?: boolean;
  hasBackground?: boolean;
}

function Container({ children, hasBackground = false }: ContainerProps) {
  const { showContent } = useExperienceStore((state) => ({
    showContent: state.showContent,
  }));

  return (
    <ContainerStyles
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      showContent={showContent}
      hasBackground={hasBackground}
    >
      {children}
    </ContainerStyles>
  );
}

function SlideContainer({ children, hasBackground = false }: ContainerProps) {
  const { showContent } = useExperienceStore((state) => ({
    showContent: state.showContent,
  }));

  const isWide = useMedia(media.bp3);

  return (
    <ContainerStyles
      initial={isWide ? { x: '-100%' } : { y: '100%' }}
      animate={isWide ? { x: '0%' } : { y: '0%' }}
      exit={isWide ? { x: '-100%' } : { y: '100%' }}
      transition={{
        duration: 0.7,
        ease: 'easeInOut',
      }}
      showContent={showContent}
      hasBackground={hasBackground}
    >
      {children}
    </ContainerStyles>
  );
}

const ContainerStyles = styled(motion.div, {
  position: 'fixed',
  display: 'flex',
  alignItems: 'center',
  left: '$6',
  bottom: 55,
  color: '$gray12',
  zIndex: 9,

  '@bp3': {
    bottom: 40,
  },

  '@bp4': {
    left: '$8',
  },

  variants: {
    isDimmed: {
      true: {
        opacity: 0.5,
      },
    },

    showContent: {
      true: {
        display: 'none',

        '@media only screen and (orientation: landscape)': {
          display: 'none',
        },

        '@bp3': {
          display: 'flex',
        },
      },
      false: {
        '@bp3': {
          display: 'flex',
        },
      },
    },

    hasBackground: {
      true: {
        backgroundColor: '$foreground',
        left: 0,
        bottom: 0,
        padding: '$8 $6',
        pt: '$8',
        widht: '100%',
        right: 0,

        '@bp3': {
          widht: 'auto',
          right: 'auto',
        },

        '@bp4': {
          padding: '$10 $8',
        },
      },
    },
  },
});

const HideButton = styled('button', {
  fontSize: '$1',
  display: 'flex',
  alignItems: 'center',
  color: '$background',
  paddingBottom: '$5',

  svg: {
    marginLeft: '$1',
  },

  '@bp3': {
    display: 'none',
  },
});

const ItemWrapper = styled('div', {
  marginBottom: '$8',
  display: 'flex',
  flexDirection: 'column',

  variants: {
    isInverted: {
      true: {
        color: '$background',
      },
    },
  },
});

const Title = styled('span', {
  fontSize: '$2',
  fontWeight: 'bold',
  marginBottom: '$2',
  color: '$foreground',
  transition: 'color .2s ease-in-out',

  variants: {
    weight: {
      regular: {
        fontWeight: 'normal',
      },
    },

    noMarign: {
      true: {
        margin: 0,
      },
    },

    isInverted: {
      true: {
        color: '$background',
      },
    },
  },
});

const TextContent = styled('span', {
  fontSize: '$1',
  color: '$foreground',

  variants: {
    isInverted: {
      true: {
        color: '$background',
      },
    },
  },
});

const Button = styled('button', {
  all: 'unset',
  position: 'relative',
  cursor: 'pointer',
  marginLeft: '$1',
  marginBottom: '$1',
  paddingBottom: '$1',
  overflow: 'hidden',

  variants: {
    isInverted: {
      true: {
        color: '$background',
      },
    },
  },

  '&:hover, &:active': {
    [`${Underline} > span`]: thinnerUnderlineHoverStyles,
  },

  '&:focus-visible': {
    ...focusStateStyles,
    outlineOffset: '2px',
    [`${Underline} > span`]: thinnerUnderlineHoverStyles,
  },
});

const CloseButtonContainer = styled('button', {
  position: 'absolute',
  top: 15,
  right: 15,
  height: 23,
  width: 23,
  color: '$background',
  transition: 'opacity 0.3s ease-in-out',

  svg: {
    width: '100%',
    height: '100%',
  },

  '&:hover': {
    opacity: 0.65,
  },
});
